import { create } from 'zustand'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export type NotificationType =
  | 'member_added'
  | 'section_assigned'
  | 'document_owner_assigned'
  | 'review_requested'
  | 'review_approved'
  | 'changes_requested'
  | 'ownership_transferred'
  | 'comment_added'
  | 'comment_reply'
  | 'comment_resolved'
  | 'mention'
  | 'key_date_changed'
  | 'analysis_ready'
  | 'feedback_resolved'
  /** A document job ended (R1 P3′). */
  | 'draft_ready'

export interface Notification {
  id: string
  organizationId: string
  userId: string
  type: NotificationType
  title: string
  link: string | null
  metadata: Record<string, unknown>
  read: boolean
  createdAt: string
}

interface NotificationsListResponse {
  items: Notification[]
  total: number
}

interface UnreadCountResponse {
  count: number
}

// ===================
// Store
// ===================

interface NotificationsState {
  notifications: Notification[]
  total: number
  unreadCount: number
  isLoading: boolean

  fetchNotifications: () => Promise<void>
  fetchUnreadCount: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAllAsRead: () => Promise<void>
}

export const useNotificationsStore = create<NotificationsState>()((set, get) => ({
  notifications: [],
  total: 0,
  unreadCount: 0,
  isLoading: false,

  fetchNotifications: async () => {
    set({ isLoading: true })
    try {
      const data = await api.get<NotificationsListResponse>('/notifications', { limit: '30' })
      set({ notifications: data.items, total: data.total, isLoading: false })
    } catch {
      set({ isLoading: false })
    }
  },

  fetchUnreadCount: async () => {
    try {
      const data = await api.get<UnreadCountResponse>('/notifications/unread-count')
      set({ unreadCount: data.count })
    } catch {
      // Silently fail — badge just won't update
    }
  },

  markAsRead: async (id: string) => {
    // Optimistic update
    const prev = get().notifications
    set({
      notifications: prev.map(n => n.id === id ? { ...n, read: true } : n),
      unreadCount: Math.max(0, get().unreadCount - (prev.find(n => n.id === id && !n.read) ? 1 : 0)),
    })

    try {
      await api.patch(`/notifications/${id}/read`)
    } catch {
      // Revert on failure
      set({ notifications: prev })
      await get().fetchUnreadCount()
    }
  },

  markAllAsRead: async () => {
    const prev = get().notifications
    set({
      notifications: prev.map(n => ({ ...n, read: true })),
      unreadCount: 0,
    })

    try {
      await api.post('/notifications/read-all')
    } catch {
      // Revert on failure
      set({ notifications: prev })
      await get().fetchUnreadCount()
    }
  },
}))
